/**
 * Tracks which (response, card) impressions have already been submitted so a
 * card renders its impression at most once per result-page response, regardless
 * of re-renders or view toggles. Server-side dedupe is authoritative for
 * delivery; this avoids redundant client submissions.
 */
export class ImpressionTracker {
  private readonly seen = new Set<string>();

  private key(responseId: string, canonicalId: string): string {
    return `${responseId}|${canonicalId}`;
  }

  /** Returns true if this is the FIRST time this card is seen for this response. */
  shouldSubmit(responseId: string, canonicalId: string): boolean {
    const k = this.key(responseId, canonicalId);
    if (this.seen.has(k)) return false;
    this.seen.add(k);
    return true;
  }

  /** Reset when a new response arrives (a new responseId scopes fresh cards). */
  reset(): void {
    this.seen.clear();
  }
}

/**
 * Batch impression tokens for first-render cards. Given a list of (canonicalId,
 * token) pairs and the tracker, returns only the tokens that should be
 * submitted (first render of each card for this response). Best-effort: the
 * caller must never block rendering on the result.
 */
export function collectFirstRenderTokens(
  responseId: string,
  cards: ReadonlyArray<{ canonicalId: string; impressionToken: string }>,
  tracker: ImpressionTracker,
): string[] {
  const tokens: string[] = [];
  for (const card of cards) {
    if (tracker.shouldSubmit(responseId, card.canonicalId)) tokens.push(card.impressionToken);
  }
  return tokens;
}
